import type { JSONSchema7, JSONSchema7Definition } from "json-schema";

import { ProcessReadInputSchema, ProcessReadOutputSchema } from "@nens/rana/api-v1";
import { objectEntries } from "@nens/shared-utils";

import { getRanaWidgetFromProperty } from "@/features/project-process-run-form";
import { DatasetByLizardIdQueryDirectly } from "@/queries/query-dataset";
import { isValidURL } from "@/utils/is-valid-url";

import { ObjectSchema } from "../types/object-schema";

import { getExtraProperties } from "./get-extra-properties";
import { isObjectSchema } from "./is-object-schema";

export const convertSchemaToRSJF = async (
  schema: ProcessReadInputSchema | ProcessReadOutputSchema,
  datasetByLizardIdQueryDirectly: DatasetByLizardIdQueryDirectly
): Promise<ObjectSchema | undefined> => {
  const schemaCopy = JSON.parse(JSON.stringify(schema)) as typeof schema;
  if (!isObjectSchema(schemaCopy)) {
    console.warn("Schema is not an object schema, cannot convert to RSJF:", schemaCopy);
    return undefined;
  }
  const newSchema: ObjectSchema = {
    ...schemaCopy,
    title: "",
    required: schema.required ? Array.from(schema.required) : undefined,
  };

  objectEntries(newSchema).forEach(([key, value]) => {
    if (!value) delete newSchema[key];
  });

  const { definitions } = newSchema;

  // Helper to resolve $ref and merge with definitions or fetched schema
  const resolveRef = async (_property: JSONSchema7Definition) => {
    if (typeof _property === "boolean")
      throw new Error("Boolean schema definitions are not supported");

    const definition = await (async () => {
      if (!_property.$ref) return;
      if (!isValidURL(_property.$ref)) {
        const defRefSplit = _property.$ref.split("/");
        const definitionsRef = defRefSplit[defRefSplit.length - 1];
        return definitions ? definitions[definitionsRef] : undefined;
      }
      const response = await fetch(_property.$ref);
      const json = await response.json();
      return json;
    })();

    if (!definition) return _property;

    const merged = { ..._property, ...definition };
    delete merged.$ref;
    merged.title = _property.title ?? merged.title;
    return merged;
  };

  // Without additionalProperties: false, RJSF can't distinguish between variants that don't match.
  // Example: if you have two anyOf/oneOf variants—one with only property 'a' and one with only property 'b' (default for a = "a", default for b = "b")
  // and the current form data is { b: "b" }, RJSF sees both variants as equally valid (neither explicitly
  // forbids 'b'). So it picks the first variant and fills in its default for 'a', resulting in { a: "a", b: "b" }.
  // This unexpected change triggers onChange even if the form is read-only.
  // Adding additionalProperties: false tells RJSF that extra keys are forbidden, so { b: "b" } only matches
  // the second variant, not the first.
  const asVariant = (def: JSONSchema7Definition): JSONSchema7Definition => {
    if (typeof def === "boolean") return def;
    return { additionalProperties: false, ...def };
  };

  // DRY for anyOf/oneOf
  const handleOf = async (arr: JSONSchema7Definition[], titles?: string[]) =>
    Promise.all(
      arr.map(async (def, idx) => asVariant(await getPropertyDefinition(def, titles?.[idx])))
    );

  // Main recursive function
  const getPropertyDefinition = async <T extends JSONSchema7Definition>(
    _property: T,
    overrideTitle?: string
  ): Promise<T> => {
    if (_property === true || _property === false) return _property;

    const property: JSONSchema7 = await getExtraProperties(
      await resolveRef(_property),
      datasetByLizardIdQueryDirectly
    );
    property.title = overrideTitle ?? property?.title ?? "";

    if (property.const) {
      property.readOnly = true;
    }

    const ranaWidget = getRanaWidgetFromProperty(property, "tabs");

    if (property.items) {
      if (Array.isArray(property.items)) {
        property.items = await handleOf(property.items, ranaWidget?.titles);
      } else {
        property.items = await getPropertyDefinition(property.items, "");
      }
    }

    if (property.anyOf) {
      property.anyOf = await handleOf(property.anyOf, ranaWidget?.titles);
    }

    if (property.oneOf) {
      property.oneOf = await handleOf(property.oneOf, ranaWidget?.titles);
    }

    if (property.properties) {
      const entries = await Promise.all(
        objectEntries(property.properties).map(async ([subKey, subProp]) => {
          if (typeof subProp === "boolean") return [subKey, undefined];
          const subPropDef = await getPropertyDefinition(subProp);
          return [subKey, { ...subProp, ...subPropDef }];
        })
      );
      property.properties = Object.fromEntries(entries.filter(([, v]) => v !== undefined));
    }

    return property as T;
  };

  // Get and aplly definitions
  return await getPropertyDefinition(newSchema);
};
